// AGC: Music Button - Slide down/up button for TopNav to play official song
// AGC: Features: Global audio instance (persists across navigation), play/pause buttons, slide animations
// AGC: Style matches tournament and emergency withdraw popups

import React, { useState, useEffect } from 'react'
import { Music, X, Play, Pause } from 'lucide-react'
import { playSoundSafe, unlockAudioPlayback, resolveAssetPath } from '../utils/audio'

// AGC: Global audio instance that persists across component unmounts
let globalMusicAudio: HTMLAudioElement | null = null
let globalIsPlaying = false
let globalListeners: Set<(playing: boolean) => void> = new Set()

// AGC: Initialize global audio instance (singleton pattern)
const getGlobalMusicAudio = (): HTMLAudioElement => {
  if (!globalMusicAudio) {
    const audioPath = resolveAssetPath('/sounds/song.mp3')
    globalMusicAudio = new Audio(audioPath)
    globalMusicAudio.volume = 0.5 // AGC: Set volume to 50%
    globalMusicAudio.loop = true // AGC: Loop the song
    
    // AGC: Handle audio events
    globalMusicAudio.addEventListener('play', () => {
      globalIsPlaying = true
      globalListeners.forEach(listener => listener(true))
    })
    globalMusicAudio.addEventListener('pause', () => {
      globalIsPlaying = false
      globalListeners.forEach(listener => listener(false))
    })
    globalMusicAudio.addEventListener('ended', () => {
      globalIsPlaying = false
      globalListeners.forEach(listener => listener(false))
    })
    globalMusicAudio.addEventListener('error', (e) => {
      console.warn('AGC: Failed to load song.mp3:', e)
      globalIsPlaying = false
      globalListeners.forEach(listener => listener(false))
    })
  }
  return globalMusicAudio
}

interface MusicButtonProps {
  // AGC: Callback to notify parent when expanded state changes (for Join Contest positioning)
  onExpandedChange?: (isExpanded: boolean) => void
}

export const MusicButton: React.FC<MusicButtonProps> = ({ onExpandedChange }) => {
  const [isPlaying, setIsPlaying] = useState(globalIsPlaying)
  const [isVisible, setIsVisible] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true) // AGC: Start expanded showing popup
  const [isHiding, setIsHiding] = useState(false) // AGC: Track slide up animation
  const [isPeekMode, setIsPeekMode] = useState(false) // AGC: Track if bar is in 50% peek mode
  const [showCloseConfirm, setShowCloseConfirm] = useState(false) // AGC: Show confirmation dialog

  // AGC: Check localStorage on mount to see if button should be visible
  useEffect(() => {
    const dismissedNever = localStorage.getItem('origami-music-popup-dismissed-never')
    const dismissedLater = localStorage.getItem('origami-music-popup-dismissed-later')
    
    if (dismissedNever) {
      // AGC: Never show again - don't show, notify parent that popup is hidden
      if (onExpandedChange) {
        onExpandedChange(false)
      }
      return
    }
    
    if (dismissedLater) {
      // AGC: "Later" means show after MainMenu loads - wait for page to be fully loaded
      // AGC: Since MusicButton is inside TopNav which is inside MainMenu, if we're here, MainMenu is loading
      // AGC: Wait for next frame to ensure MainMenu is fully rendered
      // AGC: Notify parent that popup is currently hidden (will show later)
      if (onExpandedChange) {
        onExpandedChange(false)
      }
      const timer = setTimeout(() => {
        localStorage.removeItem('origami-music-popup-dismissed-later')
        setIsVisible(true)
      }, 100) // AGC: Small delay to ensure MainMenu is fully loaded
      
      return () => clearTimeout(timer)
    } else {
      // AGC: Not dismissed - show popup immediately
      setIsVisible(true)
    }
  }, [onExpandedChange])

  // AGC: Notify parent when expanded state changes (for Join Contest positioning)
  useEffect(() => {
    if (onExpandedChange) {
      onExpandedChange(isExpanded && isVisible)
    }
  }, [isExpanded, isVisible, onExpandedChange])

  // AGC: Subscribe to global audio state changes
  useEffect(() => {
    const handleStateChange = (playing: boolean) => {
      setIsPlaying(playing)
      
      // AGC: When playing starts from play button (not popup click), go to peek mode
      // AGC: But only if we're already in the collapsed bar state
      if (playing && !isPlaying && !isExpanded) {
        setIsPeekMode(true)
      }
    }
    
    globalListeners.add(handleStateChange)
    setIsPlaying(globalIsPlaying) // AGC: Set initial state
    
    return () => {
      globalListeners.delete(handleStateChange)
    }
  }, [isPlaying, isExpanded])

  // AGC: Handle play button click
  const handlePlay = async () => {
    try {
      playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {
        // AGC: Silently fail if click sound can't play
      })

      unlockAudioPlayback() // AGC: Ensure audio is unlocked

      const audio = getGlobalMusicAudio()
      await audio.play()
    } catch (error) {
      console.warn('AGC: Failed to play audio:', error)
    }
  }

  // AGC: Handle pause button click
  const handlePause = () => {
    try {
      playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {
        // AGC: Silently fail if click sound can't play
      })

      const audio = getGlobalMusicAudio()
      audio.pause()
    } catch (error) {
      console.warn('AGC: Failed to pause audio:', error)
    }
  }

  // AGC: Handle click on popup - auto-play song and collapse to bar in peek mode
  const handlePopupClick = async () => {
    playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {})
    
    // AGC: Auto-play the song when clicking "LISTEN OFFICIAL SONG"
    try {
      unlockAudioPlayback()
      const audio = getGlobalMusicAudio()
      await audio.play()
    } catch (error) {
      console.warn('AGC: Failed to auto-play audio:', error)
    }
    
    // AGC: Collapse to bar in peek mode (50% visible)
    setIsHiding(true)
    setTimeout(() => {
      setIsExpanded(false)
      setIsHiding(false)
      setIsPeekMode(true) // AGC: Hide to 50% since music is playing
      // AGC: Notify parent that popup is now collapsed
      if (onExpandedChange) {
        onExpandedChange(false)
      }
    }, 300)
  }

  // AGC: Handle click on peek bar - expand to full
  const handlePeekClick = () => {
    playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {})
    setIsPeekMode(false) // AGC: Expand to full view
  }

  // AGC: Handle close button click - show confirmation
  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation() // AGC: Prevent triggering play
    
    playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {
      // AGC: Silently fail if click sound can't play
    })

    setShowCloseConfirm(true)
  }

  // AGC: Handle "Show Later" - dismiss until MainMenu loads
  const handleShowLater = () => {
    playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {})
    localStorage.setItem('origami-music-popup-dismissed-later', 'true')
    setShowCloseConfirm(false)
    setIsVisible(false)
    // AGC: Notify parent that popup is now hidden
    if (onExpandedChange) {
      onExpandedChange(false)
    }
  }

  // AGC: Handle "Never Show Again" - permanent dismiss
  const handleNeverShow = () => {
    playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {})
    localStorage.setItem('origami-music-popup-dismissed-never', 'true')
    localStorage.removeItem('origami-music-popup-dismissed-later') // AGC: Clear "later" if exists
    setShowCloseConfirm(false)
    setIsVisible(false)
    // AGC: Notify parent that popup is now hidden
    if (onExpandedChange) {
      onExpandedChange(false)
    }
  }

  // AGC: Handle cancel close confirmation
  const handleCancelClose = () => {
    playSoundSafe('/sounds/click.mp3', 0.3).catch(() => {})
    setShowCloseConfirm(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <>
      {/* AGC: Popup - slides down from top (like tournament popup) */}
      {isExpanded && (
        <div 
          className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-50 ${
            isHiding ? 'animate-slideUp' : 'animate-slideDown'
          }`}
        >
          <div 
            className="relative bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg shadow-lg border-2 border-purple-400 p-1"
            style={{
              animation: isHiding ? undefined : 'blinkBorder 2s infinite'
            }}
          >
            {/* AGC: Close button (X) */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCloseClick(e)
              }}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold flex items-center justify-center transition-colors z-10"
              aria-label="Close music popup"
            >
              ×
            </button>
            
            {/* AGC: LISTEN OFFICIAL SONG button */}
            <button
              onClick={handlePopupClick}
              className="flex items-center gap-2 text-white font-bold px-4 py-2 transition-all active:scale-95 whitespace-nowrap w-full"
            >
              <Music className="w-5 h-5" />
              <span>LISTEN OFFICIAL SONG</span>
            </button>
          </div>
        </div>
      )}

      {/* AGC: Collapsed bar - shows music icon, PLAY and PAUSE buttons */}
      {!isExpanded && (
        <div 
          onClick={isPeekMode ? handlePeekClick : undefined}
          className={`absolute top-0 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-lg shadow-lg bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-1 mb-1 transition-all duration-300 ${
            isPeekMode 
              ? 'translate-y-[-50%] cursor-pointer hover:translate-y-[-60%]' // AGC: 50% visible in peek mode
              : '-translate-y-full' // AGC: Full visible when not in peek mode
          }`}
          style={{
            width: '80px',
            height: '40px',
            boxShadow: '0 2px 10px rgba(168, 85, 247, 0.5)',
          }}
        >
          <Music className="w-3 h-3 text-white flex-shrink-0" />
          <div className="flex items-center gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handlePlay}
              disabled={isPlaying}
              className="flex items-center justify-center w-6 h-6 rounded bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Play official song"
            >
              <Play className="w-2.5 h-2.5 text-white fill-white" />
            </button>
            <button
              onClick={handlePause}
              disabled={!isPlaying}
              className="flex items-center justify-center w-6 h-6 rounded bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Pause official song"
            >
              <Pause className="w-2.5 h-2.5 text-white fill-white" />
            </button>
          </div>
        </div>
      )}

      {/* AGC: Close confirmation dialog */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-lg border border-purple-500/30 p-4 max-w-xs mx-4 shadow-xl">
            <h3 className="text-white font-bold text-sm mb-3">Close Music Player?</h3>
            <p className="text-slate-300 text-xs mb-4">Choose when to show again:</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleShowLater}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                Show Later (After Page Loads)
              </button>
              <button
                onClick={handleNeverShow}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                Never Show Again
              </button>
              <button
                onClick={handleCancelClose}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MusicButton

